import { useNavigate } from "react-router-dom";
import { Work } from "../types/work";
import { EntityController } from "./entityController";
import useWorkQuery from "../hooks/query/useWorkQuery";
import { extractReferenceKey } from "../components/forms/fields/basic/EntitySelectionField";
import useWorkLogic from "../hooks/logic/workLogic";

export interface WorkController extends EntityController<Work> {
  displayName: (data: Work) => string;
  viewTitle: (data: Work) => string;
  editTitle: (data: Work) => string;
}

export const useWorkController = (
  id: string | null,
  systemLanguageType: Work["system_language_type"]
): WorkController => {
  const { useFetchById, useCreate, useDeleteById, useUpdate } =
    useWorkQuery(systemLanguageType);
  const { defaultWork, metadataFieldNames, userEnums, getDisplayName, getEditTitle } =
    useWorkLogic(systemLanguageType);
  const fetchById = useFetchById(id || "");
  const update = useUpdate();
  const create = useCreate();
  const deleteById = useDeleteById();
  const navigate = useNavigate();

  const fetchData = async (): Promise<Work> => {
    if (!id) {
      return defaultWork;
    }
    const res = await fetchById.refetch();
    const workData = res.data;
    if (!workData || res.isError) {
      throw new Error("Work data not found");
    }
    return workData;
  };

  const isCreateMode = id === null;

  const displayName = (data: Work) => getDisplayName(data);

  const viewTitle = (data: Work) => displayName(data);

  const editTitle = (data: Work) => getEditTitle(data, isCreateMode);

  const onSubmit = async (data: Partial<Work>) => {
    const updateData: Partial<Work> = {
      name: data.name,
      availability: data.availability,
      keywords: data.keywords,
      compositional_form: data.compositional_form,
      work_origin: data.work_origin,
      translation_of_indic_work_id: extractReferenceKey(
        data.translation_of_indic_work
      ),
      notes: data.notes,
      internal_notes: data.internal_notes,
      entity_status_of_proofreading: data.entity_status_of_proofreading,
      attributes_metadata: {
        ...defaultWork.attributes_metadata,
        ...data.attributes_metadata,
      },
    };

    if (isCreateMode) {
      updateData.system_language_type = systemLanguageType;
    }

    let result;
    if (id) {
      result = await update.mutateAsync({
        id,
        data: { _rev: data._rev, ...updateData },
      });
      if (!result || update.isError) {
        throw new Error("Failed to update Work");
      }
    } else {
      result = await create.mutateAsync({
        ...updateData,
        system_language_type: systemLanguageType,
      });
      if (!result || create.isError) {
        throw new Error("Failed to create Work");
      }
    }
    return result;
  };

  const onDelete = async () => {
    if (!id) {
      throw new Error("Work ID is required for deletion");
    }
    await deleteById.mutateAsync(id);
    if (deleteById.isError) {
      console.error("Error deleting team:", deleteById.error);
      throw new Error("Failed to delete team");
    }
  };

  const deleteWarningMessage = "Are you sure you want to delete this work?";

  const navigateBack = () => navigate(-1);
  const getEditRoute = () => `/works/${systemLanguageType}/${id}/edit`;

  return {
    displayName,
    viewTitle,
    editTitle,
    defaultValues: defaultWork,
    metadataFieldNames,
    userEnums,
    isCreateMode,
    title: viewTitle,
    fetchData,
    onSubmit,
    onDelete,
    deleteWarningMessage,
    formCallback: navigateBack,
    getEditRoute,
  };
};

export default useWorkController;
